import { useId, useState, type ReactNode } from 'react';
import { useUiStore, type AdminLocale } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { halalasToRiyals, riyalsToHalalas } from '@/lib/price-input';
import { Textarea, cx } from './ui';

/**
 * Form fields for content an operator writes.
 *
 * The pair — Arabic beside English under one label — is the shape everything
 * bilingual uses. Writing both at once is the point: a field filled in one
 * language and forgotten in the other is a gap half the shoppers see, and
 * separate language tabs make that gap easy to miss.
 */

// The border colour is kept apart: two colour classes on one element resolve
// by stylesheet order, so an "over the limit" red could lose to the default.
// The same frame as the kit's Input (components/ui.tsx): a line, an emerald
// border and soft ring on focus in place of the page's outline.
const CONTROL_BASE =
  'min-h-10 w-full rounded-control border bg-surface px-3 text-sm text-ink shadow-[0_1px_1px_rgb(0_0_0/0.02)] transition-[border-color,box-shadow] placeholder:text-ink-faint hover:border-ink-faint/60 focus:border-brand-500 focus:ring-3 focus:ring-brand-500/20 focus:outline-none';
const CONTROL = `${CONTROL_BASE} border-line-strong`;

export interface BilingualValue {
  ar: string;
  en: string;
}

// Arabic first: it is the platform's default language (ADR-017).
const LANGUAGES = [
  { lang: 'ar', dir: 'rtl', caption: 'عربي' },
  { lang: 'en', dir: 'ltr', caption: 'English' },
] as const;

/** One label, two inputs, each in its own direction. */
export function BilingualField({
  label,
  hint,
  value,
  onChange,
  multiline = false,
  rows = 3,
  placeholder,
  placeholders,
  limit,
  compact = false,
}: {
  label: string;
  hint?: string;
  value: BilingualValue;
  onChange: (next: BilingualValue) => void;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  /** A placeholder per language — what the page shows while that language is empty. */
  placeholders?: BilingualValue;
  /**
   * The most characters the API takes. Each language counts against it as it
   * is typed and turns red past it — nothing is cut off, since a pasted
   * paragraph silently shortened is worse than one flagged as too long.
   */
  limit?: number;
  /**
   * One row of two inputs with no captions — a list's points. The label is
   * still read out, and the count shows only as the limit nears.
   */
  compact?: boolean;
}) {
  const id = useId();
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const Control = multiline ? 'textarea' : 'input';

  return (
    <div className="flex flex-col gap-1.5">
      <span className={compact ? 'sr-only' : 'text-sm font-medium text-ink'}>{label}</span>

      <div className="grid gap-2 sm:grid-cols-2">
        {LANGUAGES.map(({ lang, dir, caption }) => {
          const length = value[lang].length;
          const over = limit !== undefined && length > limit;
          const counter =
            limit !== undefined && (!compact || length >= limit * 0.8) ? (
              <span
                data-numeric
                aria-label={t('charCountLabel', { count: count(length, locale), max: count(limit, locale) })}
                className={cx('text-[11px]', over ? 'font-medium text-danger' : 'text-ink-faint')}
              >
                {t('charCount', { count: count(length, locale), max: count(limit, locale) })}
              </span>
            ) : null;

          return (
            <label key={lang} className="flex flex-col gap-1">
              {compact ? null : (
                <span className="flex items-baseline justify-between gap-2 text-[11px] text-ink-faint">
                  <span>{caption}</span>
                  {counter}
                </span>
              )}
              <Control
                id={`${id}-${lang}`}
                dir={dir}
                lang={lang}
                rows={multiline ? rows : undefined}
                value={value[lang]}
                placeholder={placeholders?.[lang] || placeholder}
                aria-label={compact ? `${label} — ${caption}` : undefined}
                aria-invalid={over || undefined}
                onChange={(event: { target: { value: string } }) => onChange({ ...value, [lang]: event.target.value })}
                className={cx(CONTROL_BASE, over ? 'border-danger' : 'border-line-strong', multiline && 'py-2')}
              />
              {compact && counter ? <span className="text-end">{counter}</span> : null}
            </label>
          );
        })}
      </div>

      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

/** A single value that reads the same in both languages. */
/** Code as written (ADR-092): monospace, left to right, no spell-check, Tab indents. */
export function CodeField({
  id,
  label,
  hint,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <Textarea
        id={id}
        dir="ltr"
        rows={8}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        className="font-mono text-xs leading-relaxed"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Tab' || event.shiftKey) return;
          event.preventDefault();
          const target = event.currentTarget;
          const { selectionStart, selectionEnd } = target;
          onChange(`${value.slice(0, selectionStart)}  ${value.slice(selectionEnd)}`);
          requestAnimationFrame(() => target.setSelectionRange(selectionStart + 2, selectionStart + 2));
        }}
      />
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

export function TextField({
  label,
  hint,
  value,
  onChange,
  placeholder,
  type = 'text',
  /** Identifiers, emails, and URLs stay Latin and left-to-right. */
  latin = false,
  inputMode,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  type?: string;
  latin?: boolean;
  inputMode?: 'numeric' | 'decimal' | 'email' | 'tel';
}) {
  const id = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        type={type}
        dir={latin ? 'ltr' : undefined}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={CONTROL}
      />
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

export function SelectField({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (next: string) => void;
}) {
  const id = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cx(CONTROL, 'pe-8')}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

/**
 * A switch.
 *
 * The whole row is the target: a 16px checkbox is a poor thing to hit, and the
 * label explains what switching it off actually does.
 */
export function ToggleField({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  // A switch at the end of the row, the whole row its target: the admin's one
  // way of saying on/off, the same as every switch in the page kit.
  // `relative` holds the visually hidden checkbox inside the row: without it the
  // checkbox is placed against the page and stretches the window past the app.
  return (
    <label className="relative flex cursor-pointer items-start justify-between gap-4 rounded-control border border-line px-3 py-2.5 transition-colors hover:bg-surface-sunken">
      <span className="min-w-0">
        <span className="block text-sm text-ink">{label}</span>
        {hint ? <span className="block text-xs text-ink-muted">{hint}</span> : null}
      </span>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className={cx(
          'relative mt-0.5 inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-600',
          checked ? 'bg-brand-600' : 'bg-line',
        )}
      >
        <span
          className={cx(
            'inline-block size-5 rounded-full bg-white shadow-card transition-transform',
            checked ? 'translate-x-[1.125rem] rtl:-translate-x-[1.125rem]' : 'translate-x-0.5 rtl:-translate-x-0.5',
          )}
        />
      </span>
    </label>
  );
}

/**
 * A few words to file something under — a customer's tags (ADR-064).
 *
 * Typed and kept as chips: Enter or a comma adds one, Backspace on an empty
 * box takes the last back, and the tags already in use are offered so the
 * store keeps to its own spellings. One per spelling regardless of case; the
 * API normalises again and is the judge.
 */
export function TagsField({
  id,
  label,
  hint,
  value,
  onChange,
  suggestions = [],
  max = 20,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string[];
  onChange: (next: string[]) => void;
  suggestions?: string[];
  max?: number;
}) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const listId = `${id}-suggestions`;

  const add = (raw: string) => {
    const tag = raw.replace(/,/g, ' ').trim().replace(/\s+/g, ' ').slice(0, 30);
    setDraft('');
    if (!tag || value.length >= max) return;
    if (value.some((entry) => entry.toLocaleLowerCase() === tag.toLocaleLowerCase())) return;
    onChange([...value, tag]);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-control border border-line bg-surface px-2 py-1.5 transition-colors focus-within:border-brand-500">
        {value.map((tag) => (
          <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-surface-sunken px-2.5 py-0.5 text-xs text-ink">
            {tag}
            <button
              type="button"
              aria-label={t('removeTag', { tag })}
              onClick={() => onChange(value.filter((entry) => entry !== tag))}
              className="text-ink-muted hover:text-danger"
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={id}
          list={listId}
          value={draft}
          maxLength={30}
          placeholder={value.length === 0 ? t('addTagPlaceholder') : ''}
          onChange={(event) => {
            // A suggestion picked from the list arrives whole: take it as a tag.
            const next = event.target.value;
            if (suggestions.includes(next)) add(next);
            else setDraft(next);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              add(draft);
            } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={() => add(draft)}
          className="min-w-28 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
        />
        <datalist id={listId}>
          {suggestions
            .filter((suggestion) => !value.some((entry) => entry.toLocaleLowerCase() === suggestion.toLocaleLowerCase()))
            .map((suggestion) => (
              <option key={suggestion} value={suggestion} />
            ))}
        </datalist>
      </div>
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

/**
 * One answer from a few, as cards — a return's disposition, a delivery method,
 * how an order is paid. The whole card is the target and the choice is a real
 * radio group, so arrows move between the options and a screen reader names
 * the question. An option may carry a line under its label and something at
 * its far end (a price); one that cannot be chosen says why in its hint.
 */
export function Choices<T extends string>({
  legend,
  hint,
  name,
  options,
  value,
  onChange,
  columns = 1,
  legendHidden = false,
}: {
  legend: string;
  hint?: string;
  name: string;
  options: { value: T; label: string; hint?: string; aside?: ReactNode; disabled?: boolean }[];
  value: T | null;
  onChange: (value: T) => void;
  /** Side by side from a small screen up; one under another on a phone. */
  columns?: 1 | 2 | 3;
  /** Under a section that already says it: the question is still named for a screen reader. */
  legendHidden?: boolean;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className={legendHidden ? 'sr-only' : 'mb-1 text-sm font-medium text-ink'}>{legend}</legend>
      {hint ? <p className="mb-1 text-xs text-ink-muted">{hint}</p> : null}
      <div className={cx('grid gap-2', columns === 2 && 'sm:grid-cols-2', columns === 3 && 'sm:grid-cols-3')}>
        {options.map((option) => (
          <label
            key={option.value}
            className={cx(
              'flex items-center gap-3 rounded-control border px-3 py-2.5 transition-colors',
              option.disabled
                ? 'cursor-not-allowed border-line opacity-60'
                : value === option.value
                  ? 'cursor-pointer border-brand-500 bg-surface-sunken ring-1 ring-brand-500'
                  : 'cursor-pointer border-line hover:bg-surface-sunken',
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              disabled={option.disabled}
              onChange={() => onChange(option.value)}
              className="size-4 shrink-0 accent-brand-600"
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm text-ink">{option.label}</span>
              {option.hint ? <span className="block text-xs text-ink-muted">{option.hint}</span> : null}
            </span>
            {option.aside ? <span className="shrink-0 text-sm text-ink">{option.aside}</span> : null}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * The days of the week as switches (R6) — a store's weekend. Stored as
 * JavaScript day numbers, 0 = Sunday; named in the reader's language.
 */
export function WeekdaysField({
  label,
  hint,
  value,
  onChange,
  locale,
}: {
  label: string;
  hint?: string;
  value: number[];
  onChange: (next: number[]) => void;
  locale: AdminLocale;
}) {
  const name = new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-SA', {
    weekday: 'long',
    timeZone: 'UTC',
  });
  // 20 September 2026 was a Sunday: day n of the week is n days after it.
  const dayName = (day: number) => name.format(new Date(Date.UTC(2026, 8, 20 + day)));

  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="mb-1.5 text-sm font-medium text-ink">{label}</legend>
      <div className="flex flex-wrap gap-2">
        {[0, 1, 2, 3, 4, 5, 6].map((day) => (
          <label
            key={day}
            className="flex cursor-pointer items-center gap-2 rounded-control border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-sunken"
          >
            <input
              type="checkbox"
              checked={value.includes(day)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...value, day].sort((a, b) => a - b)
                    : value.filter((entry) => entry !== day),
                )
              }
              className="size-4"
            />
            {dayName(day)}
          </label>
        ))}
      </div>
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </fieldset>
  );
}

/**
 * A list typed one entry per line — holiday dates (R6). The text is kept as
 * typed while editing, so a new empty line is not swallowed mid-keystroke.
 */
export function LinesField({
  id,
  label,
  hint,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [text, setText] = useState(() => value.join('\n'));

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <textarea
        id={id}
        dir="ltr"
        rows={4}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          onChange(
            event.target.value
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean),
          );
        }}
        className={`${CONTROL} py-2 font-mono`}
      />
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </div>
  );
}

/** One answer a store offers: a code it stores, and how it reads in each language. */
export interface Choice {
  code: string;
  label: BilingualValue;
}

/**
 * A short list of answers the store writes itself (R8) — "What did you make?"
 * with dress, abaya, curtains. The code is what a review stores, so it is typed
 * in Latin once and never shown; the labels are what shoppers read.
 */
export function ChoicesField({
  label,
  hint,
  value,
  onChange,
  labels,
}: {
  label: string;
  hint?: string;
  value: Choice[];
  onChange: (next: Choice[]) => void;
  labels: { code: string; arabic: string; english: string; add: string; remove: string };
}) {
  const set = (index: number, patch: Partial<Choice>) =>
    onChange(value.map((choice, position) => (position === index ? { ...choice, ...patch } : choice)));

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium text-ink">{label}</legend>
      {value.map((choice, index) => (
        <div key={index} className="grid grid-cols-[8rem_1fr_1fr_auto] items-center gap-2">
          <input
            dir="ltr"
            aria-label={labels.code}
            placeholder={labels.code}
            value={choice.code}
            onChange={(event) => set(index, { code: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
            className={`${CONTROL} font-mono`}
          />
          <input
            dir="rtl"
            lang="ar"
            aria-label={labels.arabic}
            placeholder={labels.arabic}
            value={choice.label.ar}
            onChange={(event) => set(index, { label: { ...choice.label, ar: event.target.value } })}
            className={CONTROL}
          />
          <input
            dir="ltr"
            lang="en"
            aria-label={labels.english}
            placeholder={labels.english}
            value={choice.label.en}
            onChange={(event) => set(index, { label: { ...choice.label, en: event.target.value } })}
            className={CONTROL}
          />
          <button
            type="button"
            aria-label={labels.remove}
            onClick={() => onChange(value.filter((_, position) => position !== index))}
            className="px-2 text-ink-faint transition-colors hover:text-danger"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...value, { code: '', label: { ar: '', en: '' } }])}
        className="self-start text-sm font-medium text-brand-600 hover:underline"
      >
        {labels.add}
      </button>
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </fieldset>
  );
}

/** Reads choices out of loosely-typed setting JSON, keeping only well-formed rows. */
export function readChoices(value: unknown): Choice[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry): Choice[] => {
    const record = typeof entry === 'object' && entry !== null ? (entry as Record<string, unknown>) : null;
    if (!record || typeof record.code !== 'string') return [];
    return [{ code: record.code, label: readBilingual(record.label) }];
  });
}

/** Rows without a code or any label are a half-typed line, not an answer. */
export function writeChoices(value: Choice[]): Choice[] {
  return value
    .filter((choice) => choice.code && (choice.label.ar.trim() || choice.label.en.trim()))
    .map((choice) => ({ code: choice.code, label: { ar: choice.label.ar.trim(), en: choice.label.en.trim() } }));
}

/** Reads a `{ ar, en }` value out of loosely-typed setting JSON. */
export function readBilingual(value: unknown): BilingualValue {
  if (typeof value === 'string') return { ar: value, en: value };

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    return {
      ar: typeof record.ar === 'string' ? record.ar : '',
      en: typeof record.en === 'string' ? record.en : '',
    };
  }

  return { ar: '', en: '' };
}

/** Drops an empty pair entirely rather than storing two blank strings. */
export function writeBilingual(value: BilingualValue): BilingualValue | undefined {
  if (!value.ar.trim() && !value.en.trim()) return undefined;
  return { ar: value.ar.trim(), en: value.en.trim() };
}

export function readString(value: unknown): string {
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
}

/** The direction a language's text should be typed in. */
export function directionFor(locale: AdminLocale): 'rtl' | 'ltr' {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

/** "How much do I need?" (R15): a unit, rows of a label and an amount, and a note. */
export interface QuantityGuideValue {
  unit: 'm' | 'kg' | 'l';
  /** Amounts in thousandths of the unit, as the API stores every measure (R2). */
  rows: { label: BilingualValue; amount: number }[];
  note?: BilingualValue;
}

/** Reads the guide out of loosely-typed setting JSON; null when there is none. */
export function readQuantityGuide(value: unknown): QuantityGuideValue | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const unit = record.unit === 'kg' || record.unit === 'l' ? record.unit : 'm';
  const rows = Array.isArray(record.rows)
    ? record.rows.flatMap((row) => {
        const entry = (row ?? {}) as { label?: unknown; amount?: unknown };
        return typeof entry.amount === 'number' ? [{ label: readBilingual(entry.label), amount: entry.amount }] : [];
      })
    : [];
  return { unit, rows, note: readBilingual(record.note) };
}

/**
 * The guide as a table staff type into: each amount in whole or decimal units
 * ("3", "2.5"), kept as thousandths. A half-typed amount waits for the field
 * to be left; one that is not a measure is simply not taken.
 */
export function QuantityGuideField({
  label,
  hint,
  value,
  onChange,
  toThousandths,
  toUnits,
  labels,
}: {
  label: string;
  hint?: string;
  value: QuantityGuideValue | null;
  onChange: (next: QuantityGuideValue | null) => void;
  toThousandths: (input: string) => number | null;
  toUnits: (thousandths: number) => string;
  labels: {
    unit: string;
    units: Record<QuantityGuideValue['unit'], string>;
    arabic: string;
    english: string;
    amount: string;
    add: string;
    remove: string;
    noteArabic: string;
    noteEnglish: string;
  };
}) {
  const guide: QuantityGuideValue = value ?? { unit: 'm', rows: [] };
  const commit = (next: QuantityGuideValue) =>
    // No rows and no note is no guide: the page shows nothing rather than an empty table.
    onChange(next.rows.length === 0 && !next.note?.ar.trim() && !next.note?.en.trim() ? null : next);
  const setRow = (index: number, patch: Partial<QuantityGuideValue['rows'][number]>) =>
    commit({ ...guide, rows: guide.rows.map((row, position) => (position === index ? { ...row, ...patch } : row)) });

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium text-ink">{label}</legend>
      <label className="flex items-center gap-2 text-sm text-ink-muted">
        {labels.unit}
        <select
          value={guide.unit}
          onChange={(event) => commit({ ...guide, unit: event.target.value as QuantityGuideValue['unit'] })}
          className={cx(CONTROL, 'w-auto pe-8')}
        >
          {(['m', 'kg', 'l'] as const).map((unit) => (
            <option key={unit} value={unit}>
              {labels.units[unit]}
            </option>
          ))}
        </select>
      </label>
      {guide.rows.map((row, index) => (
        <div key={index} className="grid grid-cols-[1fr_1fr_6rem_auto] items-center gap-2">
          <input
            dir="rtl"
            lang="ar"
            aria-label={labels.arabic}
            placeholder={labels.arabic}
            value={row.label.ar}
            onChange={(event) => setRow(index, { label: { ...row.label, ar: event.target.value } })}
            className={CONTROL}
          />
          <input
            dir="ltr"
            lang="en"
            aria-label={labels.english}
            placeholder={labels.english}
            value={row.label.en}
            onChange={(event) => setRow(index, { label: { ...row.label, en: event.target.value } })}
            className={CONTROL}
          />
          <input
            // Re-read when the stored amount changes; typed freely until the field is left.
            key={`${index}:${row.amount}`}
            dir="ltr"
            inputMode="decimal"
            aria-label={labels.amount}
            defaultValue={toUnits(row.amount)}
            onBlur={(event) => {
              const amount = toThousandths(event.target.value);
              if (amount !== null) setRow(index, { amount });
              else event.target.value = toUnits(row.amount);
            }}
            className={cx(CONTROL, 'font-mono')}
          />
          <button
            type="button"
            aria-label={labels.remove}
            onClick={() => commit({ ...guide, rows: guide.rows.filter((_, position) => position !== index) })}
            className="px-2 text-ink-faint transition-colors hover:text-danger"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => commit({ ...guide, rows: [...guide.rows, { label: { ar: '', en: '' }, amount: 1000 }] })}
        className="self-start text-sm font-medium text-brand-600 hover:underline"
      >
        {labels.add}
      </button>
      <div className="grid gap-2 sm:grid-cols-2">
        <input
          dir="rtl"
          lang="ar"
          aria-label={labels.noteArabic}
          placeholder={labels.noteArabic}
          value={guide.note?.ar ?? ''}
          onChange={(event) => commit({ ...guide, note: { ar: event.target.value, en: guide.note?.en ?? '' } })}
          className={CONTROL}
        />
        <input
          dir="ltr"
          lang="en"
          aria-label={labels.noteEnglish}
          placeholder={labels.noteEnglish}
          value={guide.note?.en ?? ''}
          onChange={(event) => commit({ ...guide, note: { ar: guide.note?.ar ?? '', en: event.target.value } })}
          className={CONTROL}
        />
      </div>
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </fieldset>
  );
}

/** A tier of the points programme (ADR-115), as the editor holds it. */
export interface TierRow {
  name: { ar: string; en: string };
  /** Halalas spent in the last 12 months. */
  minSpent: number;
  earnPercent: number;
}

/** Reads tiers out of loosely-typed setting JSON. */
export function readTiers(value: unknown): TierRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry): TierRow[] => {
    const record = typeof entry === 'object' && entry !== null ? (entry as Record<string, unknown>) : null;
    if (!record) return [];
    return [
      {
        name: readBilingual(record.name),
        minSpent: Number.isInteger(record.minSpent) ? (record.minSpent as number) : 0,
        earnPercent: Number.isInteger(record.earnPercent) ? (record.earnPercent as number) : 100,
      },
    ];
  });
}

/**
 * The points programme's tiers (ADR-115): a name in each language, the spend
 * over 12 months that reaches it (in riyals, kept in halalas) and the earn
 * rate as a percent of the base. At most four.
 */
export function TiersField({
  label,
  hint,
  value,
  onChange,
  labels,
}: {
  label: string;
  hint?: string;
  value: TierRow[];
  onChange: (next: TierRow[]) => void;
  labels: { arabic: string; english: string; spend: string; rate: string; add: string; remove: string };
}) {
  // What is typed in each spend box, so "1200." is not snapped to "1200.00" mid-word.
  const [spends, setSpends] = useState<string[]>(() => value.map((tier) => (tier.minSpent ? halalasToRiyals(tier.minSpent) : '')));
  const set = (index: number, patch: Partial<TierRow>) => onChange(value.map((tier, position) => (position === index ? { ...tier, ...patch } : tier)));

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium text-ink">{label}</legend>
      {value.map((tier, index) => (
        <div key={index} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2 rounded-control border border-line p-2">
          <input
            dir="rtl"
            lang="ar"
            aria-label={labels.arabic}
            placeholder={labels.arabic}
            value={tier.name.ar}
            onChange={(event) => set(index, { name: { ...tier.name, ar: event.target.value } })}
            className={CONTROL}
          />
          <input
            dir="ltr"
            lang="en"
            aria-label={labels.english}
            placeholder={labels.english}
            value={tier.name.en}
            onChange={(event) => set(index, { name: { ...tier.name, en: event.target.value } })}
            className={CONTROL}
          />
          <button
            type="button"
            aria-label={labels.remove}
            onClick={() => {
              onChange(value.filter((_, position) => position !== index));
              setSpends((current) => current.filter((_, position) => position !== index));
            }}
            className="row-span-2 self-center px-2 text-ink-faint transition-colors hover:text-danger"
          >
            ×
          </button>
          <input
            dir="ltr"
            inputMode="decimal"
            aria-label={labels.spend}
            placeholder={labels.spend}
            value={spends[index] ?? ''}
            onChange={(event) => {
              const text = event.target.value;
              setSpends((current) => current.map((entry, position) => (position === index ? text : entry)));
              set(index, { minSpent: riyalsToHalalas(text) ?? 0 });
            }}
            className={CONTROL}
          />
          <input
            dir="ltr"
            inputMode="numeric"
            aria-label={labels.rate}
            placeholder={labels.rate}
            value={String(tier.earnPercent)}
            onChange={(event) => set(index, { earnPercent: Number.parseInt(event.target.value.replace(/\D/g, ''), 10) || 0 })}
            className={CONTROL}
          />
        </div>
      ))}
      {value.length < 4 ? (
        <button
          type="button"
          onClick={() => {
            onChange([...value, { name: { ar: '', en: '' }, minSpent: 0, earnPercent: 125 }]);
            setSpends((current) => [...current, '']);
          }}
          className="self-start text-sm font-medium text-brand-600 hover:underline"
        >
          {labels.add}
        </button>
      ) : null}
      {hint ? <p className="text-xs text-ink-muted">{hint}</p> : null}
    </fieldset>
  );
}
